import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useCartContext, useWarehouseContext, useAuthContext } from "../../../app/providers/contextIndex";
import { getCatalogo } from "../../../services/backend/catalogService";
import { getAtributosFicha, getFicha } from "../../../services/backend/fichaService";
import { FALLBACK_IMAGE } from "../../../utils/uiConstants";
import {
    buildCatalogMenuStructure,
    getCatalogDiscountedPrice,
    getCatalogDiscountedPriceValue,
    getCatalogRegularPrice,
    hasCatalogDiscount,
    mapCatalogResponseProducts,
} from "../utils/catalogUtils";
import { buildCatalogSelectionBreadcrumb, resolveCatalogSelection } from "../../../shared/utils/familyMenu";
import { buildCatalogPath, slugifyRouteSegment } from "../../../shared/utils/topbarUtils";

const buildNameFromSlug = (value = "") =>
    String(value || "").replace(/-/g, " ").trim();

const useProductDetail = () => {
    const { addToCart, cotizacionId } = useCartContext();
    const { selectedAlmacen, setSelectedAlmacen, almacenes } = useWarehouseContext();
    const { isAuthenticated } = useAuthContext();
    const { dynamicFamilies } = useOutletContext() || {};
    const location = useLocation();
    const { code: legacyCode = "", productSlug = "" } = useParams();
    const [searchParams] = useSearchParams();
    const routeState = location.state || {};
    const code = String(routeState.productCode || legacyCode || "").trim();
    const collection = routeState.collection || searchParams.get("collection") || "";
    const subcollection = routeState.subcollection || searchParams.get("subcollection") || "";
    const familyId = Number(routeState.familyId || searchParams.get("familia") || 0);
    const fallbackName = routeState.productName || searchParams.get("name") || buildNameFromSlug(productSlug) || `Producto ${code}`;
    const fallbackPrice = Number(routeState.productPrice || searchParams.get("price") || 0);

    const [productData, setProductData] = useState(null);
    const [fichaData, setFichaData] = useState(null);
    const [fichaPdfData, setFichaPdfData] = useState(null);
    const [productLoading, setProductLoading] = useState(false);
    const [productError, setProductError] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [addingToCart, setAddingToCart] = useState(false);
    const toastRef = useRef(null);

    const menuStructure = useMemo(
        () => buildCatalogMenuStructure(dynamicFamilies),
        [dynamicFamilies]
    );
    const selection = useMemo(
        () => resolveCatalogSelection(menuStructure, collection, subcollection),
        [menuStructure, collection, subcollection]
    );
    const breadcrumbItems = useMemo(
        () => buildCatalogSelectionBreadcrumb(selection),
        [selection]
    );
    const catalogPath = useMemo(
        () => buildCatalogPath({ collection, subcollection, familyId, menuStructure }),
        [collection, familyId, menuStructure, subcollection]
    );

    const displayedProduct = useMemo(() => ({
        codigo: productData?.codigo || code,
        descripcion: productData?.descripcion || fallbackName,
        precio: Number(productData?.precio ?? fallbackPrice ?? 0),
        unidadMedida: productData?.unidadMedida || "",
        descuento: Number(productData?.descuento || 0),
        stock: Number(productData?.stock || 0),
        tipo: productData?.tipo || "GENÉRICO",
        image: productData?.image || FALLBACK_IMAGE,
        fotos: Array.isArray(productData?.fotos) ? productData.fotos : [],
    }), [code, fallbackName, fallbackPrice, productData]);

    const breadcrumbModel = useMemo(() => {
        const selectionItems = breadcrumbItems.length
            ? breadcrumbItems.map((item, index) => ({
                label: item.label,
                to: index === 0
                    ? ""
                    : buildCatalogPath({
                        collection: item.collection,
                        subcollection: item.subcollection,
                        familyId: item.familyId,
                        menuStructure,
                    }),
            }))
            : (collection ? [{ label: collection, to: catalogPath }] : []);

        return [
            ...selectionItems,
            { label: displayedProduct.descripcion },
        ];
    }, [breadcrumbItems, catalogPath, collection, displayedProduct.descripcion, menuStructure]);

    const productImageSrc = useMemo(
        () => String(displayedProduct.image || "").trim() || FALLBACK_IMAGE,
        [displayedProduct.image]
    );

    const hasDiscount = useMemo(
        () => hasCatalogDiscount(displayedProduct.precio, displayedProduct.descuento),
        [displayedProduct.descuento, displayedProduct.precio]
    );
    const formattedPrice = useMemo(
        () => getCatalogDiscountedPrice(displayedProduct.precio, displayedProduct.descuento),
        [displayedProduct.descuento, displayedProduct.precio]
    );
    const formattedRegularPrice = useMemo(
        () => getCatalogRegularPrice(displayedProduct.precio, displayedProduct.descuento),
        [displayedProduct.descuento, displayedProduct.precio]
    );
    const finalUnitPriceValue = useMemo(
        () => getCatalogDiscountedPriceValue(displayedProduct.precio, displayedProduct.descuento),
        [displayedProduct.descuento, displayedProduct.precio]
    );
    const selectedSucursalName = useMemo(
        () => (Array.isArray(almacenes) ? almacenes.find((item) => String(item?.almacen || "").trim() === String(selectedAlmacen || "").trim())?.sucursal : "") || selectedAlmacen || "Sin sucursal",
        [almacenes, selectedAlmacen]
    );
    const warehouseOptions = useMemo(
        () => (Array.isArray(almacenes) ? almacenes : [])
            .map((item) => ({
                label: String(item?.sucursal || item?.almacen || "").trim(),
                value: String(item?.almacen || "").trim(),
            }))
            .filter((item) => item.label && item.value),
        [almacenes]
    );

    const fallbackCartItem = useMemo(() => ({
        code: displayedProduct.codigo || code,
        name: displayedProduct.descripcion,
        price: finalUnitPriceValue,
        image: displayedProduct.image || FALLBACK_IMAGE,
        collection,
        subcollection,
        warehouse: selectedAlmacen || "Sin almacén",
    }), [
        code,
        collection,
        displayedProduct.codigo,
        displayedProduct.descripcion,
        displayedProduct.image,
        finalUnitPriceValue,
        selectedAlmacen,
        subcollection,
    ]);

    const fetchProduct = useCallback(async () => {
        const productLookup = String(code || "").trim();
        const productSlugLookup = slugifyRouteSegment(productSlug || fallbackName);
        const productSearchText = productLookup || fallbackName;

        if (!productLookup && !productSlugLookup) {
            setProductData(null);
            return;
        }
        setProductLoading(true);
        setProductError("");
        try {
            const response = await getCatalogo({
                almacen: selectedAlmacen,
                descripcion: productSearchText,
                familia: 0,
                fotos: true,
                length: 1,
                precios: true,
                start: 0,
                stocks: true,
            });
            const products = mapCatalogResponseProducts(response);
            const matchedProduct = (productLookup
                ? products.find((item) => String(item?.codigo || "").trim() === productLookup)
                : null)
                || products.find((item) => slugifyRouteSegment(item?.descripcion || "") === productSlugLookup)
                || products.find((item) => slugifyRouteSegment(item?.descripcion || "").includes(productSlugLookup))
                || null;

            if (!matchedProduct) {
                setProductData(null);
                setProductError("No fue posible obtener el detalle actualizado del producto para la sucursal seleccionada.");
                return;
            }

            setProductData(matchedProduct);

            try {
                const fichaResponse = await getAtributosFicha(matchedProduct.codigo);
                const fichaAtributos = fichaResponse?.atributos?.[0] || null;
                setFichaData(fichaAtributos ? {
                    descripcion: fichaAtributos.descripcion || "",
                    aplicacion: fichaAtributos.aplicacion || "",
                } : null);
            } catch {
                setFichaData(null);
            }

            try {
                const fichaPdfResponse = await getFicha(matchedProduct.codigo);
                if (fichaPdfResponse?.ficha && !fichaPdfResponse?.error) {
                    setFichaPdfData(fichaPdfResponse.ficha);
                } else {
                    setFichaPdfData(null);
                }
            } catch {
                setFichaPdfData(null);
            }
        } catch {
            setProductData(null);
            setProductError("No fue posible obtener el detalle actualizado del producto.");
        } finally {
            setProductLoading(false);
        }
    }, [code, fallbackName, productSlug, selectedAlmacen]);

    const handleAddToCart = useCallback(async () => {
        const productCode = String(displayedProduct.codigo || code).trim();
        if (addingToCart || !productCode) return;

        if (!cotizacionId) {
            toastRef.current?.show({
                severity: "warn",
                icon: "pi pi-exclamation-triangle",
                summary: "No hay cotización activa",
                detail: "Debes tener una cotización activa para agregar productos al carro.",
                life: 3500,
            });
            return;
        }

        setAddingToCart(true);
        try {
            const result = await addToCart(fallbackCartItem, quantity);
            if (result.success) {
                toastRef.current?.show({
                    severity: "success",
                    summary: "Agregado al carro",
                    detail: `Agregaste ${quantity} unidad${quantity > 1 ? "es" : ""}`,
                    life: 2500,
                });
            } else {
                throw new Error(result.error || "Error al agregar producto");
            }
        } catch (error) {
            toastRef.current?.show({
                severity: "warn",
                icon: "pi pi-exclamation-triangle",
                summary: "No se pudo agregar producto al carro",
                detail: error?.message || "Ocurrió un error al agregar el producto.",
                life: 3500,
            });
        } finally {
            setTimeout(() => setAddingToCart(false), 700);
        }
    }, [addToCart, cotizacionId, displayedProduct, code, fallbackCartItem, quantity, addingToCart]);

    const handleProductImageError = useCallback((event) => {
        const nextImage = event.currentTarget;
        nextImage.onerror = null;
        nextImage.src = FALLBACK_IMAGE;
    }, []);

    return {
        code,
        displayedProduct,
        fichaData,
        fichaPdfData,
        breadcrumbModel,
        productImageSrc,
        hasDiscount,
        formattedPrice,
        formattedRegularPrice,
        finalUnitPriceValue,
        selectedSucursalName,
        warehouseOptions,
        selectedAlmacen,
        setSelectedAlmacen,
        productLoading,
        productError,
        quantity,
        setQuantity,
        addingToCart,
        isAuthenticated,
        fetchProduct,
        handleAddToCart,
        handleProductImageError,
        toastRef,
    };
};

export default useProductDetail;
